import React, { useState } from 'react';
import { message, Tabs } from 'antd';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";

// 导入拆分后的组件
import UserInfoHeader from '@/components/UserInfoHeader';
import MembershipProducts from '@/components/MembershipProducts';
import BalanceRecharge from '@/components/BalanceRecharge';
import InvitePromotion from '@/components/InvitePromotion';
import PurchaseConfirmModal from '@/components/PurchaseConfirmModal';

// 导入类型定义
import type { MembershipProduct, UserInfo, InviteCode, BonusRecord } from '@/types/membership';

export default function MembershipPanel() {
  // ==================== 状态管理 ====================
  const [purchasing, setPurchasing] = useState<string | null>(null);
  
  // 弹窗状态
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<MembershipProduct | null>(null);

  // 邀请码相关状态
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [bonusRecords, setBonusRecords] = useState<BonusRecord[]>([]);
  const [generatingCode, setGeneratingCode] = useState(false);

  const queryClient = useQueryClient();

  // ==================== 数据查询 ====================
  // 获取会员产品列表
  const { data: products = [] } = useQuery<MembershipProduct[]>({
    queryKey: ["membershipProducts"],
    queryFn: async () => {
      const response = await api.get('/api/products');
      // 后端已按邀请码过滤返回商品，这里不再筛选
      return response.data as MembershipProduct[];
    },
  });

  // 获取用户信息
  const { data: userInfo, refetch: refetchUserInfo } = useQuery<UserInfo>({
    queryKey: ["userInfo"],
    queryFn: async () => {
      const response = await api.get('/api/userinfo');
      return response.data.data as UserInfo;
    }
  });

  // 获取邀请码列表
  const { refetch: refetchInviteCodes } = useQuery({
    queryKey: ["inviteCodes"],
    queryFn: async () => {
      const response = await api.get('/api/invite_codes/query');
      setInviteCodes(response.data.data);
      return response.data.data;
    }
  });

  // 获取返佣记录
  useQuery({
    queryKey: ["bonusRecords"],
    queryFn: async () => {
      const response = await api.get('/api/invite_codes/bonus_records');
      setBonusRecords(response.data.data);
      return response.data.data;
    }
  });

  // 处理购买会员
  const handlePurchase = (product: MembershipProduct) => {
    setSelectedProduct(product);
    setConfirmModalVisible(true);
  };

  // 确认购买
  const confirmPurchase = async () => {
    if (!selectedProduct) return;

    setPurchasing(selectedProduct.id);
    try {
      const response = await api.post(`/api/purchase/${selectedProduct.id}`);
      if (response.data.success) {
        message.success('购买成功！');
        queryClient.invalidateQueries({ queryKey: ["userInfo"] });
        queryClient.invalidateQueries({ queryKey: ["membershipProducts"] });
      } else {
        message.error(response.data.message || '购买失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '购买失败，请稍后重试');
    } finally {
      setPurchasing(null);
      setConfirmModalVisible(false);
      setSelectedProduct(null);
    }
  };

  // 生成邀请码
  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    try {
      await api.post('/api/invite_codes/generate');
      message.success('邀请码生成成功');
      await refetchInviteCodes();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '生成邀请码失败');
    } finally {
      setGeneratingCode(false);
    }
  };

  // 删除邀请码
  const handleDeleteCode = async (code: string) => {
    try {
      await api.delete(`/api/invite_codes/${code}`);
      message.success('邀请码删除成功');
      await refetchInviteCodes();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '删除邀请码失败');
    }
  };

  // ==================== 标签页配置 ====================
  const tabItems = [
    {
      key: '1',
      label: '会员套餐',
      children: (
        <MembershipProducts
          products={products}
          purchasing={purchasing}
          onPurchase={handlePurchase}
        />
      ),
    },
    {
      key: '2',
      label: '余额充值',
      children: <BalanceRecharge />,
    },
    {
      key: '3',
      label: '推广邀请',
      children: (
        <InvitePromotion
          inviteCodes={inviteCodes}
          bonusRecords={bonusRecords}
          generatingCode={generatingCode}
          onGenerateCode={handleGenerateCode}
          onDeleteCode={handleDeleteCode}
        />
      ),
    },
  ];

  return (
    <div className="user-stack user-membership">
      <div className="user-card-heading user-membership-heading"><h2>会员与余额</h2></div>
      {/* 用户信息头部 */}
      <UserInfoHeader 
        userInfo={userInfo} 
        onRefresh={refetchUserInfo} // 传递刷新函数
      />

      {/* 标签页内容 */}
      <Tabs
        items={tabItems}
        className="membership-tabs user-membership-tabs"
      />

      {/* 确认购买弹窗 */}
      <PurchaseConfirmModal
        isVisible={confirmModalVisible}
        selectedProduct={selectedProduct}
        userInfo={userInfo}
        onConfirm={confirmPurchase}
        onCancel={() => {
          setConfirmModalVisible(false);
          setSelectedProduct(null);
        }}
      />
    </div>
  );
}
